import { i18n } from 'src/boot/i18n'
import { PeriodType } from 'src/models/period/enums/PeriodType'
import ScheduledPeriod from 'src/models/rental/workflow/ScheduledPeriod'
import ReservationPredefinedPeriod from 'src/models/reservation/workflow/PredefinedPeriod'
import DurationPeriod from 'src/models/period/DurationPeriod'
import { frameworkQuasarConfig } from 'src/config/quasarConfig'
import {
  formatDurationHours,
  HOURS_PER_DAY,
  HOURS_PER_WEEK,
} from 'src/services/helper/duration-format'

/**
 * A duration selection that carries its own translation: rental `scheduled`
 * periods and reservation `predefined` periods. Both translation models expose
 * a `.label`, so they are handled uniformly.
 */
export type DurationModel = ScheduledPeriod | ReservationPredefinedPeriod

// Range endpoints use the app-wide datetime format, so the card matches the
// selector buttons and the order overview.
const RANGE_FORMAT = frameworkQuasarConfig.datetimeFormat

interface DurationLabelInput {
  periodType: PeriodType | null
  durationKey: string | null
}

/**
 * Derive the single-line duration label for the rental non-model cases (the store
 * renders translatable models and reservation windows itself). Branches on period
 * type:
 * - rental predefined → i18n key + count parsed from `key.count`
 * - rental period/codelocks → the composite `hours.X;days.Y;weeks.Z` key parsed
 */
export function durationLabel(input: DurationLabelInput): string | null {
  const { periodType, durationKey } = input

  if (!durationKey) {
    return null
  }

  if (periodType === PeriodType.PREDEFINED) {
    const [key, count] = durationKey.split('.')
    return i18n.global.t(
      `rental.predefined_selector.duration.${key}`,
      Number(count ?? 1),
    )
  }

  return formatComposite(durationKey)
}

// Composite rental key `hours.X;days.Y;weeks.Z` (period selector / codelocks) →
// e.g. "1 week, 2 days and 1 hour", using the same renderer as the min/max
// duration banner.
function formatComposite(durationKey: string): string | null {
  const amount = (unit: string) =>
    Number(durationKey.match(new RegExp(`${unit}\\.(\\d+)`))?.[1] ?? 0)

  const totalHours =
    amount('weeks') * HOURS_PER_WEEK +
    amount('days') * HOURS_PER_DAY +
    amount('hours')

  return formatDurationHours(totalHours) || null
}

// Renders a start→end window: each endpoint in the app datetime format, joined by
// the " ⟶ " range separator (matching the order overview's range render).
export function formatRange(period: DurationPeriod, separator = ' ⟶ '): string {
  return `${period.startDate.toFormat(
    RANGE_FORMAT,
  )}${separator}${period.endDate.toFormat(RANGE_FORMAT)}`
}
